import { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@poseidon/components/cn';
import { Button, Input } from '@poseidon/components/Input2';
import { General } from '@poseidon/components/StrokeIcons';

import { CurrencyBoundInput } from '~/components/CampaignSwitch/CurrencyBoundInput';
import { formatRangeBound, isCurrencyUnit } from '~/components/CampaignSwitch/SwitchBranchesView';

/**
 * A switch arm's range caption — click to edit its `[min, max)` bounds inline, mirroring the
 * `/switches` RangeNode. Displays as a compact pill above the arm column; clicking (a non-catch-all
 * band) opens a small "min – max" popup with Done + Delete. Currency attributes (USD) use the shared
 * `$`-prefixed field with live thousands separators and compact `$250K` / `$200M` display; other
 * attributes (Age) use a plain number field. Commits on Done / Enter / Escape / click-away.
 *
 * The catch-all ("Everything else") arm has no bounds — it renders muted and isn't editable/deletable.
 */
export type SwitchArmRangeCardProps = {
  min: number | null;
  max: number | null;
  /** Unit shown/handled in the editor (e.g. "years" / "USD"); null for dimensionless. */
  unit: string | null;
  isCatchAll: boolean;
  /** Commit edited bounds (empty input → null). Only called for non-catch-all bands. */
  onCommit: (bounds: { min: number | null; max: number | null }) => void;
  /** Remove this band. Absent for the catch-all (not deletable). */
  onRemove?: () => void;
};

const parseBound = (s: string): number | null => {
  const t = s.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

export function SwitchArmRangeCard({ min, max, unit, isCatchAll, onCommit, onRemove }: SwitchArmRangeCardProps) {
  const [editing, setEditing] = useState(false);
  // Raw digit strings (no separators) — the currency field formats them for display.
  const [minStr, setMinStr] = useState(min == null ? '' : String(min));
  const [maxStr, setMaxStr] = useState(max == null ? '' : String(max));
  const cardRef = useRef<HTMLDivElement>(null);
  const currency = isCurrencyUnit(unit);
  const boundInputClass = currency ? 'w-32' : 'w-20';

  // Keep local inputs in sync when the persisted bounds change from elsewhere (and not editing).
  useEffect(() => {
    if (editing) return;
    setMinStr(min == null ? '' : String(min));
    setMaxStr(max == null ? '' : String(max));
  }, [min, max, editing]);

  const commit = useCallback(() => {
    setEditing(false);
    onCommit({ min: parseBound(minStr), max: parseBound(maxStr) });
  }, [minStr, maxStr, onCommit]);

  // Commit + close when a press lands outside the card (click-away).
  useEffect(() => {
    if (!editing) return;
    const onDown = (e: PointerEvent) => {
      if (e.target instanceof Node && cardRef.current != null && !cardRef.current.contains(e.target)) commit();
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [editing, commit]);

  if (isCatchAll) {
    return (
      <span
        data-testid="switch-arm-range-catchall"
        className="whitespace-nowrap rounded-full border border-dashed border-lynch-300 bg-lynch-50 px-2 py-0.5 text-[11px] font-semibold text-lynch-600 shadow-sm dark:border-slate-600 dark:bg-slate-800/60 dark:text-lynch-300">
        Everything else
      </span>
    );
  }

  if (!editing) {
    // Compact, currency-aware summary: "$0 – $5M", "≥ $1M", "0 – 40", "Any".
    const lo = min == null ? '' : formatRangeBound(String(min), unit);
    const hi = max == null ? '' : formatRangeBound(String(max), unit);
    const summary =
      min == null && max == null ? 'Any' : min == null ? `< ${hi}` : max == null ? `${lo}+` : `${lo} – ${hi}`;
    return (
      <button
        type="button"
        data-testid="switch-arm-range"
        onClick={() => setEditing(true)}
        className="whitespace-nowrap rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-semibold text-blue-700 shadow-sm transition-colors hover:bg-blue-200 dark:bg-blue-500/20 dark:text-blue-300 dark:hover:bg-blue-500/30">
        {summary}
      </button>
    );
  }

  return (
    <div
      ref={cardRef}
      data-editor="switch-range"
      className="flex items-center gap-1.5 whitespace-nowrap rounded-xl border border-lynch-200 bg-white px-2.5 py-2 shadow-lg dark:border-slate-600 dark:bg-slate-900">
      {currency ? (
        <CurrencyBoundInput
          autoFocus
          value={minStr}
          onChangeRaw={setMinStr}
          onClose={commit}
          placeholder="Min"
          className={boundInputClass}
        />
      ) : (
        <Input
          autoFocus
          size="sm"
          type="number"
          min="0"
          value={minStr}
          onChange={(e) => setMinStr(e.target.value)}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === 'Escape' ? commit() : undefined)}
          placeholder="Min"
          className={boundInputClass}
        />
      )}
      <span className="text-xs font-medium text-lynch-400 dark:text-lynch-500">–</span>
      {currency ? (
        <CurrencyBoundInput
          value={maxStr}
          onChangeRaw={setMaxStr}
          onClose={commit}
          placeholder="Max"
          className={boundInputClass}
        />
      ) : (
        <Input
          size="sm"
          type="number"
          min="0"
          value={maxStr}
          onChange={(e) => setMaxStr(e.target.value)}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === 'Escape' ? commit() : undefined)}
          placeholder="Max"
          className={boundInputClass}
        />
      )}
      {unit != null && !currency && <span className="text-xs text-lynch-400 dark:text-lynch-500">{unit}</span>}
      <span className={cn('mx-0.5 h-4 w-px shrink-0 bg-lynch-200 dark:bg-slate-700')} />
      {onRemove != null && (
        <Button
          data-testid="switch-arm-range-delete"
          icon={<General.Trash01 />}
          text
          size="xs"
          color="red"
          tone="soft"
          tooltip="Delete branch"
          onClick={onRemove}
          className="shrink-0"
        />
      )}
      <Button
        data-testid="switch-arm-range-done"
        icon={<General.Check />}
        text
        size="xs"
        color="green"
        tone="soft"
        tooltip="Done"
        onClick={commit}
        className="shrink-0"
      />
    </div>
  );
}
